import type React from 'react';
import { useContext, useEffect, useState } from 'react';

import { Form } from '@zillow/constellation-10';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { WizardContext } from '../../Wizard/context';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';

import WizardAlert from '../../WizardAlert';
import PageWizard from '../../Wizard/PageWizard';
import WizardTipCardContainer from '../../WizardTipCardContainer';

import WizardTipContent from './components/WizardTipContent';

import LeaseTermsFormField from './components/LeaseTermsFormField';

import RentersInsuranceFieldSet from './components/RentersInsuranceFieldSet';

import { WizardBody, WizardFlexContainer, WizardFormSectionContainer, WizardHeading } from '../../../styles/wizard';

import type { InlineMediaQueryProp } from '@zillow/constellation';

const baseBodyText = `Help renters decide if your property’s a good fit by including key lease details like duration, fees and utility costs, and other policies.`;

type Props = {
  onGoToPrevStep: () => void;
  onGoToNextStep: () => void;
  onExitWizard?: (...args: Array<unknown>) => void;
  wizardTitle: React.ReactNode;
  progressStepper: React.ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: (typeof InlineMediaQueryProp)[keyof typeof InlineMediaQueryProp];
};

const LeaseTerms = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: Props) => {
  const {
    // @ts-expect-error
    wizardState: { isFetchingData },
  } = useContext(WizardContext);
  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);

  const leaseTermsBodyText = baseBodyText;

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    onGoToNextStep();
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }

    onGoToPrevStep();
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div className="LeaseTerms">
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        <WizardFlexContainer>
          {/* cast as div to prevent cannot appear as a descendant of form error */}
          <Form asChild={true} onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}>
            <div>
              <WizardFormSectionContainer>
                <WizardHeading level={1} textStyle="heading-sm">
                  What should renters know about the lease terms?
                </WizardHeading>

                <WizardBody>{leaseTermsBodyText}</WizardBody>

                <LeaseTermsFormField />

                <RentersInsuranceFieldSet />
              </WizardFormSectionContainer>
            </div>
          </Form>

          <WizardTipCardContainer
            header="Tips for lease descriptions"
            showDesktopViewBreakpoint={showDesktopViewBreakpoint}
          >
            <WizardTipContent />
          </WizardTipCardContainer>
        </WizardFlexContainer>
      </div>
    </PageWizard>
  );
};

export default LeaseTerms;
